import React, { useState } from 'react';
import styled from 'styled-components';
import { ButtonSwitch } from 'components';
import { Tag } from 'components/Tag';
import { lineHeight, Size, theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import {
  CalculatorParticipantType,
  CalculatorSplitMethod,
  DEFAULT_POOL_NAME,
  ICalculatorRule,
  ICalculatorRuleSharePolicyShare,
  ICategoryGroup,
  IRemainderBeneficiary,
  ITipCalculatorResultContribution,
  ITipCalculatorResultEmployee,
  ITipCalculatorResultReceived,
  ShareEligibilityMethod,
  ShareRemainderType,
  ShareRuleType,
  TipCalculatorReceivedDistributionType,
} from 'types';
import { buildSalesItems, centsToDollarsDisplay, Duration, getOwnerRule, getTipSources, toPercent, toTitleCase } from 'utils';
import ContributingBreakdown from './ContributingBreakdown';
import ReceivingBreakdown from './ReceivingBreakdown';

interface IToggleableBreakdownProps {
  categoryGroups: ICategoryGroup[];
  calculatorRules: ICalculatorRule[];
  calculation: ITipCalculatorResultEmployee;
  creditCardFee?: number;
  className?: string;
  contributingOnly?: boolean;
  dataCy?: string;
  segmentName: string;
  disabled?: boolean;
}

const BreakdownContainer = styled.div``;

const StyledButtonSwitch = styled(ButtonSwitch)`
  @media (max-width: 500px) {
    justify-content: space-between;
  }

  @media (max-width: 350px) {
    button {
      padding: ${toRem(theme.space.xs)} ${toRem(theme.space.sm)};
      /* off-scale exception: no font-size token below xxs (11px) */
      font-size: ${toRem(10)};
    }
  }
`;

const ContributingTag = styled(Tag)`
  color: ${theme.color.white};
  background-color: ${theme.color.secondary[400]};
  border-radius: ${toRem(4)};
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
`;

const ToggleableBreakdownLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
`;

const Header = styled.div`
  display: flex;
  flex-flow: column-reverse nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};

  @media (min-width: 500px) {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const SegmentInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: ${toRem(theme.space.lg)} 0 0 0;

  @media (min-width: 500px) {
    margin: 0 ${toRem(theme.space.lg)} 0 0;
  }
`;

const SegmentName = styled.div`
  margin-bottom: ${toRem(theme.space.xs)};
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.bold};
`;

const EmployeeSegmentInfo = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;

  span {
    padding: 0 ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &:not(:last-child) {
      border-right: 1px solid ${theme.color.neutral[200]};
    }
  }
`;

const Semibold = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

const RECEIVING_LINE_ITEM_TYPES: ShareRuleType[] = [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP, ShareRuleType.TIP];

const displayEligibilityMethod = (contribution: ITipCalculatorResultContribution): string => (contribution.eligibilityMethod === ShareEligibilityMethod.BY_SEGMENT ? 'segment' : 'check');

const buildContributingDescription = (contribution: ITipCalculatorResultContribution, beneficiary?: ICalculatorRuleSharePolicyShare): React.ReactNode => {
  if (!beneficiary) return <></>;
  const sources = contribution.type === ShareRuleType.TIP ? getTipSources(beneficiary.sources) : toTitleCase(contribution.category);
  return (
    <span>
      earning <Semibold>{toPercent(contribution.share)}</Semibold> of <Semibold>{sources}</Semibold>
      {' per'} {displayEligibilityMethod(contribution)} ({centsToDollarsDisplay(contribution.eligibleAmount)})
    </span>
  );
};

const displaySplitMethod = (splitMethod: CalculatorSplitMethod): string => {
  if (splitMethod === CalculatorSplitMethod.EVENLY) return ' split evenly';
  if (splitMethod === CalculatorSplitMethod.TIME_WORKED) return ' split by time worked';
  return '';
};

const buildShareReceivedDescription = (received: ITipCalculatorResultReceived, beneficiary?: ICalculatorRuleSharePolicyShare): React.ReactNode => {
  if (!beneficiary) return <></>;
  const sources = [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP].includes(received.type) ? toTitleCase(received.category) : getTipSources(beneficiary.sources);
  return (
    <span>
      earning <Semibold>{toPercent(received.share)}</Semibold> of <Semibold>{sources}</Semibold>
      {displaySplitMethod(received.split)}
    </span>
  );
};

const buildRemainderReceivedDescription = (received: ITipCalculatorResultReceived, beneficiary?: IRemainderBeneficiary): React.ReactNode => {
  if (!beneficiary) return <></>;
  const sources = getTipSources(beneficiary.sources);
  const condition = received.conditions?.[0];
  return (
    <span>
      earning{' '}
      {condition && (
        <span>
          <Semibold>{condition.hours} hours</Semibold> of{' '}
        </span>
      )}
      <Semibold>{sources}</Semibold>
      {displaySplitMethod(received.split)}
    </span>
  );
};

const ToggleableBreakdown: React.FC<IToggleableBreakdownProps> = ({
  categoryGroups,
  calculatorRules,
  calculation,
  creditCardFee,
  className = '',
  contributingOnly = false,
  dataCy = 'employee-tips-toggleable-breakdown',
  segmentName,
  disabled = false,
}) => {
  const [switchChecked, setSwitchChecked] = useState(!contributingOnly); // true === receiving, false === contributing
  const onSwitchChange = () => setSwitchChecked(!switchChecked);

  const ownerRule = getOwnerRule(calculatorRules, calculation.provider?.id);

  // Build Contributing -> Collected
  const collectedLineItems: ITipsBreakdownLineItem[] = [
    { label: 'CC Tips', amount: calculation.totals.tip.creditCard || 0 },
    { label: 'CC Autograts', amount: calculation.totals.autoGratuity.creditCard || 0 },
    ...(calculation.totals.tip.creditCardFee || calculation.totals.autoGratuity.creditCardFee
      ? [
          {
            label: 'CC Fees',
            amount: -(calculation.totals.tip.creditCardFee || 0) + -(calculation.totals.autoGratuity.creditCardFee || 0),
            ...(creditCardFee && { description: `(${creditCardFee}%)` }),
          },
        ]
      : []),
    { label: 'Cash Tips', amount: calculation.totals.tip.cash || 0 },
    { label: 'Cash Autograts', amount: calculation.totals.autoGratuity.cash || 0 },
    { label: 'Net', amount: calculation.totals.net },
  ];

  // Build Contributing -> Contributing To
  const contributingToLineItems = (contributions: ITipCalculatorResultContribution[]): ITipsBreakdownLineItem[] => {
    const calculationJobTitle = calculation.job.title.trim().toLowerCase();
    const rule = ownerRule || calculatorRules.find((r) => r.contributor && r.contributor.name.trim().toLowerCase() === calculationJobTitle);

    return (contributions || [])
      .map((contribution) => {
        const jobTitles = contribution.beneficiaries.map((b) => toTitleCase(b.name));
        const normalizedJobTitles = new Set(jobTitles.map((j) => j.trim().toLowerCase()));
        const beneficiary = rule?.sharePolicy.shares.find((s) => s.beneficiaries.every((b) => normalizedJobTitles.has(b.name.trim().toLowerCase())));
        const label = jobTitles.join(' & ');

        return { label, amount: contribution.total, description: buildContributingDescription(contribution, beneficiary) };
      })
      .sort((a, b) => b.amount - a.amount);
  };

  // Build Contributing -> Sales
  const salesLineItems = (calculation: ITipCalculatorResultEmployee, categoryGroups: ICategoryGroup[], rules: ICalculatorRule[]): ITipsBreakdownLineItem[] => {
    const includeSalesLineItems = rules.some((rule) => rule.sharePolicy?.shares.some((share) => [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP].includes(share.type)));

    if (!includeSalesLineItems) return undefined;

    return buildSalesItems(calculation, categoryGroups);
  };

  // Build Contributing -> Remainder
  const remainderLineItems = (): ITipsBreakdownLineItem[] => {
    const calculationJobTitle = calculation.job.title.trim().toLowerCase();
    let result: ITipsBreakdownLineItem[] = [];

    const rule = ownerRule || calculatorRules.find((rule) => rule.sharePolicy && rule.contributor && rule.contributor.name.toLowerCase() === calculationJobTitle);
    if (rule?.sharePolicy.remainder.type === ShareRemainderType.SELF) {
      result = [
        { amount: calculation.retained.tips || 0, label: `Retained Tips by ${calculation.name}` },
        { amount: calculation.retained.autoGratuities || 0, label: `Retained Autograts by ${calculation.name}` },
      ];
    } else if (rule?.sharePolicy.remainder.type === ShareRemainderType.POOL) {
      const contributionTotal = (calculation.contributions || []).reduce((acc, contribution) => acc + contribution.total, 0);
      const pooledJobs = rule.sharePolicy.remainder.beneficiaries.flatMap((beneficiary) => beneficiary.participants.map((p) => toTitleCase(p.name)));
      result = [
        {
          amount: calculation.totals.net ? calculation.totals.net - contributionTotal : 0,
          label: 'Pooled',
          description: pooledJobs.join(' & '),
        },
      ];
    }
    return result;
  };

  // Build Receiving -> Receiving From
  const receivingLineItems = (): ITipsBreakdownLineItem[] => {
    const result: ITipsBreakdownLineItem[] = [];
    const calculationJobTitle = calculation.job.title.trim().toLowerCase();

    if (calculation.retained.tips) {
      result.push({ label: 'Retained Tips', amount: calculation.retained.tips });
    }
    if (calculation.retained.autoGratuities) {
      result.push({ label: 'Retained Autograts', amount: calculation.retained.autoGratuities });
    }

    let defaultPoolTotal = 0;
    let hasDefaultPool = false;

    calculation.received?.forEach((received) => {
      if (received.type === ShareRuleType.DEFAULT_POOL) {
        defaultPoolTotal += received.total;
        hasDefaultPool = true;
        return;
      }

      if (!RECEIVING_LINE_ITEM_TYPES.includes(received.type)) return;

      const receivedFromName = received.from.name.trim().toLowerCase();
      const isReceivingFromOwner = received.from.type === CalculatorParticipantType.EMPLOYEE;
      const rule = calculatorRules.find((rule) => {
        if (isReceivingFromOwner) {
          return rule.contributor?.type === CalculatorParticipantType.EMPLOYEE && rule.contributor.providerId === received.from.providerId;
        }
        return rule.contributor?.name.trim().toLowerCase() === receivedFromName;
      });
      const label = toTitleCase(receivedFromName);

      if (received.distribution === TipCalculatorReceivedDistributionType.SHARE) {
        const beneficiary = rule?.sharePolicy.shares.find((s) => s.beneficiaries.some((b) => b.name.trim().toLowerCase() === calculationJobTitle));

        result.push({ label, amount: received.total, description: buildShareReceivedDescription(received, beneficiary) });
        return;
      }

      // REMAINDER is only valid with type TIP
      if (received.type === ShareRuleType.TIP && received.distribution === TipCalculatorReceivedDistributionType.REMAINDER) {
        const beneficiary = rule?.sharePolicy.remainder.beneficiaries.find((b) => b.participants.some((p) => p.name.trim().toLowerCase() === calculationJobTitle));

        result.push({ label, amount: received.total, description: buildRemainderReceivedDescription(received, beneficiary) });
      }
    });

    if (hasDefaultPool) {
      result.push({ label: DEFAULT_POOL_NAME, amount: defaultPoolTotal });
    }

    return result;
  };

  return (
    <ToggleableBreakdownLayout className={className} data-cy={dataCy}>
      <Header data-cy={`${dataCy}-header`}>
        <SegmentInfo>
          <SegmentName data-cy={`${dataCy}-segment-name`}>{segmentName}</SegmentName>
          {ownerRule ? (
            <Tag label="Owner Rule" size={Size.SMALL} />
          ) : (
            <EmployeeSegmentInfo>
              <span data-cy={`${dataCy}-job-title`}>{toTitleCase(calculation.job.title)}</span>
              {!contributingOnly && <span data-cy={`${dataCy}-duration`}>{new Duration(calculation.totals.totalMinWorked).toString()}</span>}
            </EmployeeSegmentInfo>
          )}
        </SegmentInfo>

        {contributingOnly ? (
          <ContributingTag dataCy={`${dataCy}-contributing`} label="Contributing" />
        ) : (
          <StyledButtonSwitch dataCy={`${dataCy}-switch`} onChange={onSwitchChange} checked={switchChecked} leftText="Contributing" rightText="Receiving" data-cy="toggleable-breakdown-switch" disabled={disabled} />
        )}
      </Header>

      <BreakdownContainer>
        {switchChecked ? (
          <ReceivingBreakdown values={receivingLineItems()} />
        ) : (
          <ContributingBreakdown
            collectedLineItems={collectedLineItems}
            salesLineItems={salesLineItems(calculation, categoryGroups, calculatorRules)}
            contributingLineItems={calculation.contributions ? contributingToLineItems(calculation.contributions) : []}
            remainderLineItems={remainderLineItems()}
          />
        )}
      </BreakdownContainer>
    </ToggleableBreakdownLayout>
  );
};

export default ToggleableBreakdown;
